import type { MetricStats } from '@erectus/shared';

/** Linear-interpolated percentile over an unsorted sample. */
export function percentile(values: readonly number[], q: number): number {
  if (values.length === 0) return Number.NaN;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0]!;
  const pos = (sorted.length - 1) * Math.min(1, Math.max(0, q));
  const lower = Math.floor(pos);
  const upper = Math.ceil(pos);
  const weight = pos - lower;
  return sorted[lower]! * (1 - weight) + sorted[upper]! * weight;
}

export function mean(values: readonly number[]): number {
  if (values.length === 0) return Number.NaN;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

/** null when nothing measurable contributed — the UI must say "not measured". */
export function metricStats(values: readonly number[]): MetricStats | null {
  const clean = values.filter((v) => Number.isFinite(v));
  if (clean.length === 0) return null;
  return {
    mean: round2(mean(clean)),
    p10: round2(percentile(clean, 0.1)),
    p50: round2(percentile(clean, 0.5)),
    p90: round2(percentile(clean, 0.9)),
    n: clean.length,
  };
}

export function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

export function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}
